import FullCalendar from '@fullcalendar/react';
import { WebPageEvents } from '@griply/types';
import {
  differenceInDays,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { useCalendarTaskSync } from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSortedSmartLists } from 'features/smart-lists';
import { useSortedTags } from 'features/tags';
import { useTaskTodayControls } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarSlotContextMenu } from 'shared/components/connected/calendar-slot-context-menu';
import { FullCalendarMonth } from 'shared/components/ui/full-calendar-month';
import { weekdaysNumberMap } from 'shared/constants';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useCalendarEventControls } from 'shared/hooks/use-calendar-event-controls';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { Timestamp } from 'shared/types/timestamp';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { getScheduleByDate } from 'shared/utils/schedules';

import { useUpcomingCalendarEvents } from '../../hooks/use-upcoming-calendar-events';
import * as Styled from './month.style';

const eventClasNames = [Styled.EventContainer.styledComponentId];

export type MonthProps = {
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
  referenceDate?: Timestamp;
};

export const Month: React.FC<MonthProps> = ({
  fullCalendarRef,
  referenceDate,
}) => {
  useTrackPageVisitEvent(WebPageEvents.UpcomingMonth, undefined);
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const links = useEntityLinks();
  const tags = useSortedTags();
  const smartLists = useSortedSmartLists();
  const today = useToday();
  const openTaskDetail = useOpenTaskDetail();
  const { onComplete } = useTaskTodayControls();
  const navigate = useNavigate();

  const startDate = useMemo(
    () => startOfMonth(referenceDate || today),
    [referenceDate, today],
  );
  const dayRange = useMemo(
    () => differenceInDays(endOfMonth(startDate), startDate) + 1,
    [startDate],
  );
  const externalStartDate = useMemo(
    () =>
      startOfWeek(startDate, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }),
    [startDate, weekStartsOn],
  );
  const externalDayRange = useMemo(
    () =>
      differenceInDays(
        endOfWeek(endOfMonth(startDate), {
          weekStartsOn: weekdaysNumberMap[weekStartsOn],
        }),
        externalStartDate,
      ),
    [externalStartDate, startDate, weekStartsOn],
  );

  // Month renders a 6-week grid (leading/trailing days), so use a wider buffer.
  const { tasks: filteredTasks, events } = useUpcomingCalendarEvents({
    startDate,
    dayRange,
    bufferWeeks: 4,
    externalStartDate,
    externalDayRange,
  });

  const { onEventChange, onEventReceive, onDateTime, slotContextMenu } =
    useCalendarEventControls(lifeAreas, goals, filteredTasks, tags, smartLists);

  const _openEventDetail = useCallback(
    (id: ID) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (task) {
        openTaskDetail(task);
        return;
      }

      const goal = goals.find(({ id: goalId }) => goalId === id);
      if (goal) {
        navigate(getGoalDetailUrl(goal));
        return;
      }

      const lifeArea = lifeAreas.find(
        ({ id: lifeAreaId }) => lifeAreaId === id,
      );
      if (lifeArea) {
        navigate(replaceUrlParams(Paths.LifeAreaDetail, { id: lifeArea.id }));
        return;
      }
    },
    [filteredTasks, goals, lifeAreas, openTaskDetail, navigate],
  );
  const calendarSync = useCalendarTaskSync(filteredTasks);

  const _onComplete = useCallback(
    (id: ID, date: Timestamp) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (!task) {
        return;
      }
      const daySchedule = getScheduleByDate(task.schedules, { date, today });
      onComplete(task, ScheduleEntryState.Complete, {
        referenceDate: date,
        toggleDayEntry: !!daySchedule?.frequency,
      });
    },
    [onComplete, filteredTasks, today],
  );

  return (
    <>
      <FullCalendarMonth
        fullCalendarRef={fullCalendarRef}
        tasks={filteredTasks}
        goals={goals}
        lifeAreas={lifeAreas}
        links={links}
        tags={tags}
        smartLists={smartLists}
        weekStartsOn={weekStartsOn}
        onCompleteTask={_onComplete}
        onClickEvent={_openEventDetail}
        calendarSync={calendarSync}
        referenceDate={referenceDate}
        events={events}
        eventClassNames={eventClasNames}
        eventChange={onEventChange}
        eventReceive={onEventReceive}
        dateClick={onDateTime}
        eventResizableFromStart={false}
        eventDurationEditable={false}
        editable
        droppable
      />

      {slotContextMenu.slot && <CalendarSlotContextMenu {...slotContextMenu} />}
    </>
  );
};
